import { PageLink } from '@ipfabric/ui-components/Link/Link';
import {
  type BgpRoutingColumnValue,
  type TableColumnFormatBgpRouting,
  getValueFromKeyOfType,
} from '@ipfabric/utils';
import type { RenderedTableCell } from 'components/Table/cells/RenderedTableCell';
import {
  ARRAY_CELL_MAX_ITEMS,
  maybeWrapWithMore,
} from 'components/Table/cells/helpers/trimLargeArray';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import type { ReactNode } from 'react';
import { unsafeWrapWithReactKeyByIndex } from 'utils/react/unsafeWrapWithReactKeyByIndex';
import type { RenderCellContentArguments } from '../model/TableCellContentRenderFunction';

const makeGetBgpRoutingContent =
  (
    toWebString: (value: BgpRoutingColumnValue) => string,
    sn: string,
    contrast: boolean,
  ) =>
  (value: BgpRoutingColumnValue): ReactNode => {
    const label = toWebString(value);
    switch (value.type) {
      case 'route-map':
      case 'route-policy':
        return (
          <PageLink
            accent={contrast ? 'contrast' : 'positive'}
            to={`/technology/routing/policy/policies?options=${encodeURIComponent(
              JSON.stringify({
                filters: {
                  sn: ['eq', sn],
                  name: ['eq', value.name],
                  type: ['eq', value.type],
                },
              }),
            )}`}
          >
            {label}
          </PageLink>
        );
      case 'filter-policy':
        return (
          <PageLink
            accent={contrast ? 'contrast' : 'positive'}
            to={`/technology/security/access-list?options=${encodeURIComponent(
              JSON.stringify({
                filters: {
                  sn: ['eq', sn],
                  policyName: ['eq', value.name],
                },
              }),
            )}`}
          >
            {label}
          </PageLink>
        );
      default:
        return label;
    }
  };

export const renderBgpRoutingCell = <TRow,>(
  format: TableColumnFormatBgpRouting<TRow>,
  { row, context, severity }: RenderCellContentArguments<TRow>,
): RenderedTableCell => {
  const content = getValueFromKeyOfType(row, format.field);
  const sn = getValueFromKeyOfType(row, format.snField);

  if (!content || !sn) {
    return {
      content: null,
    };
  }

  const formatValue = context.disableLinks
    ? format.toWebString
    : makeGetBgpRoutingContent(format.toWebString, sn, severity !== null);

  return {
    content: Array.isArray(content)
      ? pipe(
          content,
          (a) => a.slice(0, ARRAY_CELL_MAX_ITEMS),
          array.map(formatValue),
          (a) => a.map(unsafeWrapWithReactKeyByIndex),
          array.reduce<ReactNode, ReactNode | null>(null, (prev, current) =>
            prev === null ? [current] : [prev, ', ', current],
          ),
          maybeWrapWithMore(content),
        )
      : formatValue(content),
  };
};
